/**
 * Utilities for extracting resolved data model IDs from KGV2 nodes on AI suggestions.
 *
 * Estate V2 holdings suggestions carry an explicit `entity` edge for ownership.
 * KGV2 statement traversal remains available for other relationships, such as a
 * liability's creditor, and for onboarding principal resolution. Onboarding suggestions use KGV2 nodes to resolve principal references for the
 * onboarding modal. Other suggestion kinds (entity details, subtrust details, DP
 * recipients) now carry an explicit `entity`/`testamentaryEntity` edge set by the v2
 * save layer, so they no longer need anything in this module to resolve ownership.
 *
 * Recipient resolution for the AI suggestion matcher (clientProfile/entity/etc.)
 * still uses these helpers, but always passes `referenceType` explicitly.
 */

import { first } from 'lodash';

import type {
  AiSuggestionForReviewCard,
  SuggestionForSearchAndEvidence,
} from '@/modules/aiSuggestions/aiSuggestions.contracts';
import { AiSuggestionKind, Kgv2NodeNodeType } from '@/types/schema';
import { diagnostics } from '@/utils/diagnostics';
import { getNodes } from '@/utils/graphqlUtils';

import { AI_ONBOARDING_SUGGESTION_KINDS } from './aiSuggestions.types';

/**
 * Minimal resolved node interface — only `id` is required.
 * Display fields are optional since they're only used for debug logging in the grouping path.
 * Both the full `AiSuggestion_Kgv2NodeResolvedFragment` and the lite `EstatePage_KGV2NodeLite`
 * fragment satisfy this interface via structural typing.
 */
interface KGV2NodeForGroupingMinimumResolved {
  id: string;
  subtype?: { displayName?: string | null } | null;
  displayName?: string | null;
  name?: string | null;
}

/**
 * Minimal KGV2 node interface for grouping purposes.
 * Requires resolved-entity connections but only needs IDs from them.
 */
export interface KGV2NodeForGrouping {
  id: string;
  nodeType?: Kgv2NodeNodeType;
  namePrimary: string;
  resolvedEntities?: {
    edges?: Array<{
      node?: KGV2NodeForGroupingMinimumResolved | null;
    } | null> | null;
  } | null;
  resolvedClientProfiles?: {
    edges?: Array<{
      node?: KGV2NodeForGroupingMinimumResolved | null;
    } | null> | null;
  } | null;
  resolvedClientOrganizations?: {
    edges?: Array<{
      node?: KGV2NodeForGroupingMinimumResolved | null;
    } | null> | null;
  } | null;
  resolvedTestamentaryEntities?: {
    edges?: Array<{
      node?: KGV2NodeForGroupingMinimumResolved | null;
    } | null> | null;
  } | null;
  onboardingSuggestions?: {
    edges?: Array<{
      node?: Pick<SuggestionForKGV2Grouping, 'id' | 'kind'> | null;
    } | null> | null;
  } | null;
}

/**
 * Minimal KGV2 statement interface for grouping purposes.
 */
interface KGV2StatementForGrouping {
  id: string;
  predicate: string;
  subject: KGV2NodeForGrouping;
  object?: KGV2NodeForGrouping | null;
}

/**
 * Minimal suggestion interface for the KGV2-statement-based resolvers below.
 * Only requires the fields those resolvers actually read; both the full
 * `CommonAiSuggestion_WithKgv2Fragment` and the lite
 * `EstatePage_UnacknowledgedAiSuggestionFragment` satisfy it structurally.
 */
export interface SuggestionForKGV2Grouping {
  id: string;
  kind: AiSuggestionKind;
  kgV2Statements?: {
    edges?: Array<{ node?: KGV2StatementForGrouping | null } | null> | null;
  } | null;
}

/**
 * Holdings suggestion kinds whose KGV2 statements place a related entity in the
 * SUBJECT position:
 *   - Assets:        [Owner Entity] --owns--> [Asset]
 *   - Liabilities:   [Debtor/Creditor] --is_debtor_of/is_creditor_of--> [Liability]
 *   - Life Insurance:[Owner Entity] --owns--> [Policy]
 *
 * Used only by callers that are intentionally resolving a statement relationship.
 * Estate list/count grouping uses the explicit `entity`/`testamentaryEntity` edge.
 */
const HOLDINGS_KINDS_OWNER_AS_SUBJECT = new Set<AiSuggestionKind>([
  AiSuggestionKind.Asset,
  AiSuggestionKind.Liability,
  AiSuggestionKind.LifeInsurance,
]);

/**
 * Returns the KGV2 statement position (subject/object) where the related entity sits.
 *
 * Only holdings kinds are mapped today.
 */
function getEntityReferenceTypeForSuggestionKind(
  kind: AiSuggestionKind
): 'subject' | 'object' | undefined {
  return HOLDINGS_KINDS_OWNER_AS_SUBJECT.has(kind) ? 'subject' : undefined;
}

/**
 * Resolved data from a KGV2 node.
 */
export interface ResolvedDataFromKGV2Node {
  /** The resolved data model ID (entity, client profile, etc.), if resolved */
  id: string;
  /** The display name of the resolved entity/client profile, if resolved */
  displayName: string;
}

export type ResolvedDataKind =
  | 'entity'
  | 'testamentaryEntity'
  | 'clientProfile'
  | 'clientOrganization';

/**
 * Represents a resolved principal from an onboarding entity suggestion's KGV2 data.
 */
export interface ResolvedPrincipalFromKGV2 {
  /** The original KGV2 node ID from principalNodeIDs */
  nodeId: string;
  /** The KGV2 node type, when the node was found in the suggestion graph */
  nodeType: Kgv2NodeNodeType | undefined;
  /** The display name from the KGV2 node (namePrimary) */
  nodeName: string;
  /** The resolved data model ID (entity, client profile, etc.), if resolved */
  resolvedId: string | undefined;
  /** The display name of the resolved entity/client profile, if resolved */
  resolvedDisplayName: string | undefined;
  /** Whether this principal has been resolved to a data model record */
  isResolved: boolean;
  /** The linked onboarding suggestion ID for this KGV2 node, if one exists */
  referenceSuggestionID: string | undefined;
  /** The linked onboarding suggestion kind for this KGV2 node, if one exists */
  referenceSuggestionKind: AiSuggestionKind | undefined;
}

/**
 * Extracts the first resolved data model ID and display name from a KGV2 node.
 *
 * Checks the node's resolved relationships in the following priority order:
 * 1. Entities (uses subtype.displayName)
 * 2. Testamentary Entities (uses displayName)
 * 3. Client Profiles (uses displayName)
 * 4. Client Organizations (uses name)
 *
 * @param node - The KGV2 node to extract resolved data from
 * @returns Object containing the first resolved ID and display name found
 */
export function getFirstResolvedDataFromNode(
  node: KGV2NodeForGrouping,
  resolvedDataKinds: ResolvedDataKind[] = [
    'entity',
    'testamentaryEntity',
    'clientProfile',
    'clientOrganization',
  ]
): ResolvedDataFromKGV2Node | undefined {
  for (const resolvedDataKind of resolvedDataKinds) {
    if (resolvedDataKind === 'entity') {
      const entity = first(getNodes(node.resolvedEntities));
      if (entity?.id) {
        // The Entity type has no top-level displayName; its label always comes
        // from the subtype (e.g. IrrevocableTrust.displayName).
        return {
          id: entity.id,
          displayName: entity.subtype?.displayName || node.namePrimary,
        };
      }
    }

    if (resolvedDataKind === 'testamentaryEntity') {
      const testamentaryEntity = first(
        getNodes(node.resolvedTestamentaryEntities)
      );
      if (testamentaryEntity?.id) {
        return {
          id: testamentaryEntity.id,
          displayName: testamentaryEntity?.displayName || node.namePrimary,
        };
      }
    }

    if (resolvedDataKind === 'clientProfile') {
      const clientProfile = first(getNodes(node.resolvedClientProfiles));
      if (clientProfile?.id) {
        return {
          id: clientProfile.id,
          displayName: clientProfile?.displayName || node.namePrimary,
        };
      }
    }

    if (resolvedDataKind === 'clientOrganization') {
      const clientOrganization = first(
        getNodes(node.resolvedClientOrganizations)
      );
      if (clientOrganization?.id) {
        return {
          id: clientOrganization.id,
          displayName: clientOrganization?.name || node.namePrimary,
        };
      }
    }
  }

  return undefined;
}

interface GetOwningEntityFromSuggestionOpts {
  /**
   * Explicitly specify the reference type. If not provided, the function will
   * auto-determine based on the suggestion kind using the kind-to-reference-type mapping.
   */
  referenceType?: 'subject' | 'object' | 'both';
  /**
   * Filter statements by predicate(s). Only statements with predicates in this array
   * will be considered. If not provided, all statements are considered.
   */
  predicates?: string[];
  /**
   * Restrict which resolved record kinds count as a match, in priority order.
   * Defaults to the legacy order: entity, testamentaryEntity, clientProfile, clientOrganization.
   */
  resolvedDataKinds?: ResolvedDataKind[];
}

/**
 * Predicate for liability debtor relationship.
 * Statement structure: [Debtor Entity] --is_debtor_of--> [Liability]
 */
export const LIABILITY_DEBTOR_PREDICATE = 'is_debtor_of';

/**
 * Predicate for liability creditor relationship.
 * Statement structure: [Creditor Entity] --is_creditor_of--> [Liability]
 */
export const LIABILITY_CREDITOR_PREDICATE = 'is_creditor_of';

/**
 * Resolves the owning entity for a liability suggestion using the debtor-first fallback pattern.
 *
 * For liabilities, we first try to resolve the debtor (is_debtor_of predicate), and if that
 * fails, fall back to the creditor (is_creditor_of predicate). This establishes a consistent
 * "owner" for grouping and validation purposes.
 *
 * @param suggestion - The AI suggestion containing KGV2 statements
 * @returns The resolved entity ID, or undefined if neither debtor nor creditor can be resolved
 */
export function getLiabilityOwnerEntityFromSuggestionStatements(
  suggestion: SuggestionForKGV2Grouping
): string | null | undefined {
  // Try debtor first (is_debtor_of predicate)
  const debtorEntityID = getOwningEntityFromSuggestionStatements(suggestion, {
    predicates: [LIABILITY_DEBTOR_PREDICATE],
  });

  if (debtorEntityID) {
    return debtorEntityID;
  }

  // Fall back to creditor (is_creditor_of predicate)
  return getOwningEntityFromSuggestionStatements(suggestion, {
    predicates: [LIABILITY_CREDITOR_PREDICATE],
  });
}

/**
 * Extracts a resolved data model ID from an AI suggestion's KGV2 statements.
 *
 * Two intended call patterns:
 *
 * 1. **Holdings ownership** — callers omit `referenceType` and rely on the kind
 *    mapping (Asset / Liability / LifeInsurance all resolve from the subject node).
 *    Returns the owning entity ID.
 *
 * 2. **Recipient/actor resolution** — callers (matcher, subtrust modal) pass
 *    `referenceType` explicitly to extract the resolved person/entity from a known
 *    statement position, regardless of the suggestion kind.
 *
 * Onboarding suggestions short-circuit and return `null` — they create entities,
 * they don't belong to one.
 *
 * @returns The first resolved data model ID found, `null` for onboarding suggestions,
 *          or `undefined` if nothing resolves or the kind isn't a holdings kind and
 *          no `referenceType` was provided.
 */
export function getOwningEntityFromSuggestionStatements(
  suggestion: SuggestionForKGV2Grouping,
  opts?: GetOwningEntityFromSuggestionOpts
): string | null | undefined {
  // Don't process onboarding suggestions - they create entities, not belong to them
  if (AI_ONBOARDING_SUGGESTION_KINDS.includes(suggestion.kind)) {
    diagnostics.debug(
      '[KGV2Resolution] Skipping onboarding suggestion - they create entities, not belong to them',
      { suggestionId: suggestion.id, kind: suggestion.kind }
    );
    return null;
  }

  // Determine reference type: use explicit option or auto-detect from kind
  const referenceType =
    opts?.referenceType ??
    getEntityReferenceTypeForSuggestionKind(suggestion.kind);

  // If we can't determine the reference type, return undefined
  if (!referenceType) {
    diagnostics.warn(
      '[KGV2Resolution] No reference type for suggestion kind - kind is not a holdings kind and no explicit referenceType was passed',
      { suggestionId: suggestion.id, kind: suggestion.kind }
    );
    return undefined;
  }

  const allStatements = getNodes(suggestion.kgV2Statements);

  // Filter statements by predicate if specified
  const statements = opts?.predicates
    ? allStatements.filter((s) => opts.predicates!.includes(s.predicate))
    : allStatements;

  if (statements.length === 0) {
    diagnostics.debug(
      '[KGV2Resolution] No KGV2 statements found on suggestion',
      {
        suggestionId: suggestion.id,
        kind: suggestion.kind,
        predicateFilter: opts?.predicates,
        totalStatements: allStatements.length,
      }
    );
    return undefined;
  }

  for (const statement of statements) {
    const nodes =
      referenceType === 'both'
        ? [
            { node: statement.subject, nodePosition: 'subject' as const },
            { node: statement.object, nodePosition: 'object' as const },
          ]
        : [
            {
              node:
                referenceType === 'subject'
                  ? statement.subject
                  : statement.object,
              nodePosition: referenceType,
            },
          ];

    for (const { node, nodePosition } of nodes) {
      if (!node) {
        diagnostics.debug(
          `[KGV2Resolution] Statement missing ${nodePosition} node`,
          {
            suggestionId: suggestion.id,
            kind: suggestion.kind,
            statementId: statement.id,
            predicate: statement.predicate,
          }
        );
        continue;
      }

      const resolvedData = getFirstResolvedDataFromNode(
        node,
        opts?.resolvedDataKinds
      );
      if (resolvedData?.id) {
        diagnostics.debug(
          '[KGV2Resolution] Resolved owning entity from suggestion',
          {
            suggestionId: suggestion.id,
            kind: suggestion.kind,
            referenceType,
            nodePosition,
            resolvedId: resolvedData.id,
            nodeId: node.id,
            nodeName: node.namePrimary,
            resolvedDataKinds: opts?.resolvedDataKinds,
          }
        );
        return resolvedData.id;
      }
    }
  }

  diagnostics.debug(
    '[KGV2Resolution] No resolved entity found in any statement node',
    {
      suggestionId: suggestion.id,
      kind: suggestion.kind,
      referenceType,
      statementCount: statements.length,
    }
  );
  return undefined;
}

export function isKGV2PersonPrincipalNodeType(
  nodeType: Kgv2NodeNodeType | undefined
): boolean {
  return nodeType === Kgv2NodeNodeType.Person;
}

export function isKGV2EntityPrincipalNodeType(
  nodeType: Kgv2NodeNodeType | undefined
): boolean {
  return (
    nodeType === Kgv2NodeNodeType.BusinessEntity ||
    nodeType === Kgv2NodeNodeType.DonorAdvisedFund ||
    nodeType === Kgv2NodeNodeType.PersonalAccount ||
    nodeType === Kgv2NodeNodeType.PrivateFoundation ||
    nodeType === Kgv2NodeNodeType.SubTrust ||
    nodeType === Kgv2NodeNodeType.Trust
  );
}

/**
 * Resolves a list of `principalNodeIDs` to their data-model records via a direct
 * id->node lookup.
 *
 * This is the preferred way to resolve onboarding principals. `principalNodeIDs`
 * is already the backend's authoritative list of an entity's principals
 * (grantors/owners/donors), so we simply look each ID up in the household's KG
 * V2 node map and read its resolution edges — no statement/predicate traversal,
 * unlike {@link resolvePrincipalNodesFromOnboardingSuggestion}.
 *
 * `nodesById` should contain every node referenced by `principalNodeIDs`. In the
 * onboarding modal it is populated by the `FetchOnboardingPrincipalNodes` query,
 * which is server-filtered to exactly these IDs. Any ID missing from the map
 * resolves to an unresolved stub (so the UI can flag it for review).
 *
 * @param principalNodeIDs - The KG V2 node IDs from `onboardingEntity.principalNodeIDs`
 * @param nodesById - Map of KG V2 node ID -> resolved node
 * @returns One ResolvedPrincipalFromKGV2 per input ID, preserving order
 */
export function resolvePrincipalsFromNodeMap(
  principalNodeIDs: string[],
  nodesById: Map<string, KGV2NodeForGrouping>
): ResolvedPrincipalFromKGV2[] {
  return principalNodeIDs.map((nodeId): ResolvedPrincipalFromKGV2 => {
    const node = nodesById.get(nodeId);

    if (!node) {
      return {
        nodeId,
        nodeType: undefined,
        nodeName: '',
        resolvedId: undefined,
        resolvedDisplayName: undefined,
        isResolved: false,
        referenceSuggestionID: undefined,
        referenceSuggestionKind: undefined,
      };
    }

    const resolvedData = getFirstResolvedDataFromNode(node);
    const referenceSuggestion = first(getNodes(node.onboardingSuggestions));

    return {
      nodeId,
      nodeType: node.nodeType,
      nodeName: node.namePrimary,
      resolvedId: resolvedData?.id,
      resolvedDisplayName: resolvedData?.displayName,
      isResolved: !!resolvedData,
      referenceSuggestionID: referenceSuggestion?.id,
      referenceSuggestionKind: referenceSuggestion?.kind,
    };
  });
}

/**
 * Gets the initial document ID from a suggestion for opening the AIOnboardingModal.
 *
 * File paragraphs point at the underlying PDF document.
 *
 * @param suggestion - The AI suggestion to extract document ID from
 * @returns The document ID if found, undefined otherwise
 */
export function getInitialDocumentIdFromSuggestion(
  suggestion: SuggestionForSearchAndEvidence | undefined | null
): string | undefined {
  if (!suggestion) {
    return undefined;
  }

  for (const evidence of suggestion.evidence ?? []) {
    for (const paragraph of getNodes(evidence.fileParagraphs)) {
      const docId = paragraph.file?.pdfDocument?.id;
      if (docId) {
        return docId;
      }
    }
  }

  return undefined;
}

/**
 * Extract the first KGV2 source node ID from an AI suggestion.
 * Returns undefined if there are no source KGV2 nodes.
 *
 * This is only relevant for onboarding-type suggestions (ONBOARDING_INDIVIDUAL,
 * ONBOARDING_ORGANIZATION, ONBOARDING_ENTITY, ONBOARDING_TESTAMENTARY_ENTITY)
 * and holdings-type suggestions (ASSET, LIABILITY, LIFE_INSURANCE).
 *
 * @param suggestion - The AI suggestion to extract KGV2 node ID from
 * @returns The first KGV2 node ID, or undefined if not applicable
 */
export function getKgV2NodeIDFromSuggestion(
  suggestion: AiSuggestionForReviewCard | undefined
): string | undefined {
  if (!suggestion) {
    return undefined;
  }

  const sourceNode = first(getNodes(suggestion.sourceKgV2Nodes));
  if (!sourceNode) {
    return undefined;
  }

  return sourceNode.id;
}
